import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import AdminTable from '../../components/Admin/AdminTable';
import ExcelUploadModal from '../../components/Admin/ExcelUploadModal';

const SAMPLE_COLS = ['spare_item','make','part_code','uom','delhi','bangalore'];
const EMPTY_FORM = { spare_item:'', make:'', part_code:'', uom:'Nos', is_active:true };

export default function Spares() {
  const [data, setData] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [spares, whs] = await Promise.all([
        client.get('/admin/spares/'),
        client.get('/admin/warehouses/'),
      ]);
      setData(Array.isArray(spares.data) ? spares.data : spares.data.results ?? []);
      setWarehouses(Array.isArray(whs.data) ? whs.data : whs.data.results ?? []);
    } catch { setError('Failed to load spares.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build dynamic columns including per-warehouse stock
  const tableCols = [
    { key:'spare_item', label:'Spare Item' },
    { key:'make', label:'Make' },
    { key:'part_code', label:'Part Code' },
    { key:'uom', label:'UOM' },
    ...warehouses.map(wh => ({
      key: `stock_${wh.code}`,
      label: wh.code,
      render: (_, row) => {
        const entry = (row.stock || []).find(s => s.warehouse_code === wh.code || s.warehouse === wh.id);
        return entry ? entry.quantity ?? entry.qty ?? 0 : 0;
      },
    })),
    { key:'is_active', label:'Active', render: v => (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{v ? 'Yes' : 'No'}</span>
    )},
  ];

  const openAdd = () => { setEditRow(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row) => { setEditRow(row); setForm({ spare_item: row.spare_item, make: row.make, part_code: row.part_code, uom: row.uom, is_active: row.is_active }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editRow) await client.put(`/admin/spares/${editRow.id}/`, form);
      else await client.post('/admin/spares/', form);
      setShowForm(false); load();
    } catch (err) { alert(err?.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await client.delete(`/admin/spares/${deleteRow.id}/`); setDeleteRow(null); load(); }
    catch { alert('Delete failed.'); }
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data: res } = await client.post('/admin/upload/spares/', fd);
    load(); return res;
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-purple-300';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Spare Parts Master</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage spare parts catalogue and stock</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Upload Excel
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Spare
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <AdminTable columns={tableCols} data={data} loading={loading} onEdit={openEdit} onDelete={setDeleteRow} />
      </div>

      {showUpload && <ExcelUploadModal title="Spare Parts" onUpload={handleUpload} onClose={() => setShowUpload(false)} sampleColumns={SAMPLE_COLS} templateEntity="spares" />}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{editRow ? 'Edit Spare Part' : 'Add Spare Part'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <p className="text-xs text-purple-600 bg-purple-50 rounded-lg px-3 py-2">Stock quantities are managed via Excel upload.</p>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Spare Item *</label><input className={inp} value={form.spare_item} onChange={e => set('spare_item', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Make</label><input className={inp} value={form.make} onChange={e => set('make', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Part Code</label><input className={inp} value={form.part_code} onChange={e => set('part_code', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">UOM</label><input className={inp} value={form.uom} onChange={e => set('uom', e.target.value)} placeholder="Nos" /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="spare_active" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} className="w-4 h-4 accent-purple-600" />
                <label htmlFor="spare_active" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">{saving ? 'Savingâ€¦' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Delete Spare Part</h3>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteRow.spare_item}</strong>?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteRow(null)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

